import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Users, Phone, FileText, Check, Loader2, Search, Target, Wallet,
  Plus, Calendar, MessageSquare, Instagram, Flame, TrendingUp, Rocket,
  AlertCircle, Clock, Sparkles, ChevronRight, Flag, ArrowRight, X,
  PhoneCall, StickyNote, Settings, ExternalLink, GripVertical, Bell,
  RotateCcw, PauseCircle, BarChart3, Trophy, Euro, Mail, PieChart as PieChartIcon, Shield
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import {
  DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable,
  DragOverlay, closestCenter, pointerWithin
} from "@dnd-kit/core";

// ============================================================
// Config
// ============================================================
const STAGES = [
  { key: "new", label: "Nuovi Lead", color: "bg-slate-500", light: "bg-slate-100 dark:bg-slate-800/50" },
  { key: "contacted", label: "Contattati", color: "bg-blue-500", light: "bg-blue-50 dark:bg-blue-950/30" },
  { key: "interested", label: "Interessati", color: "bg-emerald-500", light: "bg-emerald-50 dark:bg-emerald-950/30" },
  { key: "demo_booked", label: "Demo Fissata", color: "bg-violet-500", light: "bg-violet-50 dark:bg-violet-950/30" },
  { key: "trial", label: "Trial / Chiusura", color: "bg-amber-500", light: "bg-amber-50 dark:bg-amber-950/30" },
  { key: "closed_won", label: "Chiuso ✓", color: "bg-emerald-600", light: "bg-emerald-50 dark:bg-emerald-950/40", terminal: true },
  { key: "recall_later", label: "Da risentire più avanti", color: "bg-orange-500", light: "bg-orange-50 dark:bg-orange-950/30", parking: true },
  { key: "no_reply", label: "Mai Risposto", color: "bg-rose-500", light: "bg-rose-50 dark:bg-rose-950/30", parking: true },
  { key: "not_interested", label: "Non Interessato", color: "bg-zinc-500", light: "bg-zinc-50 dark:bg-zinc-900/40", parking: true },
];

const NOT_INTERESTED_REASONS = [
  { key: "already_uses_software", label: "Già usa un software", emoji: "🛠️" },
  { key: "price", label: "Prezzo", emoji: "💸" },
  { key: "not_priority", label: "Non è una priorità", emoji: "🗓️" },
  { key: "recall_future", label: "Richiamare in futuro", emoji: "⏰" },
  { key: "other", label: "Altro", emoji: "💬" },
];

const NOT_INTERESTED_REASON_MAP = NOT_INTERESTED_REASONS.reduce((acc, r) => { acc[r.key] = r; return acc; }, {});

const SECTORS = [
  { value: "dentista", label: "Dentista" },
  { value: "estetica", label: "Centro Estetico" },
  { value: "parrucchiere", label: "Parrucchiere" },
  { value: "barbiere", label: "Barber" },
  { value: "fisioterapia", label: "Fisioterapia" },
  { value: "osteopata", label: "Osteopata" },
  { value: "nutrizionista", label: "Nutrizionista" },
  { value: "psicologo", label: "Psicologo" },
  { value: "medico", label: "Medico" },
  { value: "fitness", label: "Fitness" },
  { value: "altro", label: "Altro" },
];

const SOURCES = [
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "whatsapp", label: "WhatsApp", icon: MessageSquare },
  { value: "facebook", label: "Facebook", icon: MessageSquare },
  { value: "tiktok", label: "TikTok", icon: MessageSquare },
  { value: "email", label: "Mail", icon: Mail },
  { value: "cold_call", label: "Call a freddo", icon: PhoneCall },
  { value: "outbound_manual", label: "Altro", icon: Phone },
];

const TEMPLATES = {
  ig_first: {
    name: "Instagram DM",
    icon: Instagram,
    text: "Ciao {{nome}}, ho visto il tuo studio e volevo chiederti se usi già un sistema automatico per prenotazioni e richiami pazienti? Ho qualcosa che potrebbe aiutarti molto.",
  },
  wa_demo: {
    name: "WhatsApp Demo",
    icon: MessageSquare,
    text: "Ciao {{nome}}, ti mostro una demo gratuita di SmartBooker — il sistema che automatizza reception, WhatsApp e recall per studi del tuo settore. Hai 2 minuti ora? https://smartbooker.it/demo-odonto",
  },
  followup: {
    name: "Follow-up",
    icon: Clock,
    text: "Ciao {{nome}}, ti ricontatto solo per sapere se hai avuto modo di dare un'occhiata a SmartBooker. Se vuoi, ti richiamo io quando preferisci.",
  },
};

// Follow-up engine: days → color
const staleClass = (lead) => {
  const last = lead.last_entry_at || lead.updated_at || lead.created_at;
  if (!last) return "border-transparent";
  const days = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
  if (days >= 10) return "border-red-500 ring-2 ring-red-500/40";
  if (days >= 5) return "border-red-400";
  if (days >= 2) return "border-amber-400";
  return "border-transparent";
};

const hotScore = (lead) => {
  // crude heuristic: interest_level high + status interested/demo + has entries
  let score = 0;
  if (lead.interest_level === "alto") score += 2;
  if (lead.interest_level === "medio") score += 1;
  if (lead.status === "interested") score += 1;
  if (lead.status === "demo_booked") score += 2;
  if (lead.status === "trial") score += 3;
  return score;
};

// ============================================================
// Device detection: desktop (hover + pointer fine) → drag-and-drop
// mobile/touch → quick-move buttons
// ============================================================
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)");
    const h = (e) => setIsDesktop(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", h);
    else mq.addListener(h);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", h);
      else mq.removeListener(h);
    };
  }, []);
  return isDesktop;
};

// Fire-and-forget tracking helper (never blocks UI)
const trackEvent = (payload) => {
  try {
    axios.post(`${API}/sales/tracking/event`, payload, { withCredentials: true }).catch(() => {});
  } catch { /* noop */ }
};

// ============================================================
// Main
// ============================================================
const SalesDashboardV2 = () => {
  const { user } = useAuth();
  const { language } = useLanguage();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Search / filters
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");

  // New lead dialog
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", referent_name: "", instagram_username: "", phone: "", email: "",
    sector: "dentista", city: "", interest_level: "medio",
    source: "instagram", notes: "",
  });

  // Duplicate protection
  const [dupCheck, setDupCheck] = useState(null); // { duplicates, severity, can_force }
  const [dupOpen, setDupOpen] = useState(false);
  const [checkingDup, setCheckingDup] = useState(false);

  // Stage change handler
  const [movingLeadId, setMovingLeadId] = useState(null);
  const [detailLead, setDetailLead] = useState(null);

  // Drag & drop (desktop only)
  const isDesktop = useIsDesktop();
  const [activeDragLead, setActiveDragLead] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  // Track time spent per stage per lead (stage enter timestamp in ms)
  const stageEnterRef = useRef({});  // { [lead_id]: { stage, at } }

  // Not-interested flow
  const [notInterestedDialog, setNotInterestedDialog] = useState(null); // { leadId, fromStage }

  // Closed-won deal info flow
  const [closedWonDialog, setClosedWonDialog] = useState(null); // { leadId, fromStage }

  // Not-interested analytics
  const [niStats, setNiStats] = useState(null);
  const fetchNiStats = async () => {
    try {
      const r = await axios.get(`${API}/sales/analytics/not-interested?period=30`, { withCredentials: true });
      setNiStats(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchNiStats(); /* eslint-disable-next-line */ }, []);

  // ------------ Fetch ------------
  const fetch = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/sales/my/leads`, { withCredentials: true }),
        axios.get(`${API}/sales/my/stats`, { withCredentials: true }),
      ]);
      setLeads(leadsRes.data.leads || []);
      setStats(statsRes.data);
    } catch {
      toast.error(language === "it" ? "Errore caricamento" : "Loading error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, []);

  // ------------ New lead ------------
  const isSuperAdmin = user?.role === "super_admin";

  const resetForm = () => setForm({
    name: "", referent_name: "", instagram_username: "", phone: "", email: "",
    sector: "dentista", city: "", interest_level: "medio",
    source: "instagram", notes: "",
  });

  const submitCreate = async ({ force = false } = {}) => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/sales/my/leads`,
        { ...form, force_create: force },
        { withCredentials: true }
      );
      toast.success(language === "it" ? "Lead creato" : "Lead created");
      setNewLeadOpen(false);
      setDupOpen(false);
      setDupCheck(null);
      resetForm();
      fetch();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "duplicate_blocked") {
        // Server-side block (race-condition safety net): show modal
        setDupCheck({
          duplicates: detail.duplicates || [],
          severity: detail.severity || "block",
          can_force: isSuperAdmin,
        });
        setDupOpen(true);
        toast.error(language === "it" ? "Lead duplicato bloccato" : "Duplicate blocked");
      } else {
        toast.error(language === "it" ? "Errore" : "Error");
      }
    } finally {
      setSaving(false);
    }
  };

  const createLead = async () => {
    if (!form.name.trim()) {
      toast.error(language === "it" ? "Nome attività obbligatorio" : "Business name required");
      return;
    }
    // Pre-check duplicates
    setCheckingDup(true);
    try {
      const res = await axios.post(
        `${API}/sales/leads/check-duplicate`,
        {
          name: form.name,
          phone: form.phone,
          email: form.email,
          instagram_username: form.instagram_username,
          city: form.city,
        },
        { withCredentials: true }
      );
      const { duplicates = [], severity = "clean", can_force = false } = res.data || {};
      if (severity === "clean" || duplicates.length === 0) {
        await submitCreate();
        return;
      }
      // Show modal — user decides
      setDupCheck({ duplicates, severity, can_force });
      setDupOpen(true);
    } catch {
      // If pre-check fails, fall through to create attempt (server still enforces)
      await submitCreate();
    } finally {
      setCheckingDup(false);
    }
  };

  // Reassign request (sales)
  const requestReassign = async (lead_id) => {
    try {
      await axios.post(
        `${API}/sales/my/leads/${lead_id}/request-reassign`,
        { reason: `Duplicate detected during outreach: ${form.name}` },
        { withCredentials: true }
      );
      toast.success(language === "it" ? "Richiesta inviata al super admin" : "Request sent");
    } catch (err) {
      const msg = err?.response?.data?.detail || (language === "it" ? "Errore" : "Error");
      toast.error(typeof msg === "string" ? msg : "Errore");
    }
  };

  // ------------ Move stage ------------
  const moveStage = async (leadId, newStatus, { via = "button", payload = null } = {}) => {
    const lead = leads.find(l => l.lead_id === leadId);
    const fromStage = lead?.status;
    if (!lead || fromStage === newStatus) return;

    // Intercept: moving TO not_interested requires a reason → open dialog (skip if payload already provided)
    if (newStatus === "not_interested" && !payload) {
      setNotInterestedDialog({ leadId, fromStage, via });
      return;
    }
    // Intercept: closed_won requires deal info
    if (newStatus === "closed_won" && !payload) {
      setClosedWonDialog({ leadId, fromStage, via, leadName: lead.name });
      return;
    }

    setMovingLeadId(leadId);
    try {
      const body = { status: newStatus, ...(payload || {}) };
      await axios.patch(`${API}/sales/my/leads/${leadId}`, body, { withCredentials: true });
      setLeads(prev => prev.map(l => l.lead_id === leadId ? {
        ...l,
        status: newStatus,
        ...(payload?.not_interested_reason ? {
          not_interested_reason: payload.not_interested_reason,
          not_interested_reason_label: NOT_INTERESTED_REASON_MAP[payload.not_interested_reason]?.label,
          not_interested_at: new Date().toISOString(),
          recall_at: payload.recall_at || null,
        } : {}),
        ...(fromStage === "not_interested" && newStatus !== "not_interested" ? { recovered_at: new Date().toISOString() } : {}),
      } : l));

      // Fire analytics: stage_time_spent
      const prev = stageEnterRef.current[leadId];
      if (prev && prev.stage === fromStage) {
        trackEvent({
          event_type: "stage_time_spent",
          lead_id: leadId,
          from_stage: fromStage,
          to_stage: newStatus,
          duration_ms: Date.now() - prev.at,
          meta: { via },
        });
      }
      trackEvent({
        event_type: "lead_stage_changed",
        lead_id: leadId,
        from_stage: fromStage,
        to_stage: newStatus,
        meta: { via, ...(payload?.not_interested_reason ? { reason: payload.not_interested_reason } : {}) },
      });
      stageEnterRef.current[leadId] = { stage: newStatus, at: Date.now() };
      // Refresh not-interested analytics if relevant
      if (newStatus === "not_interested" || fromStage === "not_interested") {
        fetchNiStats();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "reason_required") {
        // Server-side fallback: open dialog
        setNotInterestedDialog({ leadId, fromStage, via });
      } else if (detail?.code === "deal_info_required" || detail?.code === "custom_amount_required") {
        setClosedWonDialog({ leadId, fromStage, via, leadName: lead.name });
      } else {
        toast.error(language === "it" ? "Errore spostamento" : "Move error");
      }
    } finally {
      setMovingLeadId(null);
    }
  };

  // Submit "not interested" with reason from dialog
  const submitNotInterested = async ({ reason, note, recallAt }) => {
    if (!notInterestedDialog) return;
    const { leadId, via } = notInterestedDialog;
    setNotInterestedDialog(null);
    await moveStage(leadId, "not_interested", {
      via,
      payload: {
        not_interested_reason: reason,
        not_interested_note: note || "",
        ...(reason === "recall_future" && recallAt ? { recall_at: recallAt } : {}),
      },
    });
  };

  // Submit "closed_won" with deal info
  const submitClosedWon = async (dealInfo) => {
    if (!closedWonDialog) return;
    const { leadId, via } = closedWonDialog;
    setClosedWonDialog(null);
    await moveStage(leadId, "closed_won", { via, payload: dealInfo });
    toast.success(language === "it"
      ? `Deal chiuso! Revenue €${(dealInfo._estimated_revenue || 0).toLocaleString("it-IT")}`
      : "Deal closed!");
  };

  // Reactivate (from not_interested → interested)
  const reactivateLead = (leadId) => moveStage(leadId, "interested", { via: "reactivate" });

  // ------------ DnD handlers (desktop) ------------
  const onDragStart = (ev) => {
    const lead = leads.find(l => l.lead_id === ev.active.id);
    if (lead) {
      setActiveDragLead(lead);
      trackEvent({ event_type: "lead_dragged", lead_id: lead.lead_id, from_stage: lead.status });
    }
  };
  const onDragOver = (ev) => {
    const over = ev.over?.id;
    setHoverStage(over && STAGES.find(s => s.key === over) ? over : null);
  };
  const onDragEnd = (ev) => {
    const leadId = ev.active?.id;
    const overId = ev.over?.id;
    setActiveDragLead(null);
    setHoverStage(null);
    if (!leadId || !overId) return;
    if (!STAGES.find(s => s.key === overId)) return;
    moveStage(leadId, overId, { via: "drag" });
  };

  // ------------ Derived data ------------
  const filteredLeads = useMemo(() => {
    return leads.filter(l => {
      if (sectorFilter !== "all" && l.sector !== sectorFilter) return false;
      if (search && !`${l.name} ${l.referent_name || ""} ${l.city || ""} ${l.phone || ""}`.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [leads, search, sectorFilter]);

  const byStage = useMemo(() => {
    const m = {};
    STAGES.forEach(s => { m[s.key] = []; });
    filteredLeads.forEach(l => { if (m[l.status]) m[l.status].push(l); });
    return m;
  }, [filteredLeads]);

  const kpis = useMemo(() => {
    const thisMonth = new Date();
    thisMonth.setDate(1); thisMonth.setHours(0,0,0,0);
    const ms = thisMonth.getTime();
    const inserted = leads.filter(l => new Date(l.created_at || 0).getTime() >= ms).length;
    const contacted = leads.filter(l => ["contacted", "interested", "demo_booked", "trial", "closed"].includes(l.status)).length;
    const interested = leads.filter(l => ["interested", "demo_booked", "trial", "closed"].includes(l.status)).length;
    const demos = leads.filter(l => l.status === "demo_booked" || l.status === "trial" || l.status === "closed").length;
    const closed = leads.filter(l => l.status === "closed").length;
    return { inserted, contacted, interested, demos, closed };
  }, [leads]);

  const actionCenter = useMemo(() => {
    const overdue = leads.filter(l => {
      const last = l.last_entry_at || l.updated_at || l.created_at;
      if (!last) return false;
      const days = (Date.now() - new Date(last).getTime()) / (1000 * 60 * 60 * 24);
      return days >= 5 && !["closed", "deleted"].includes(l.status);
    }).length;
    const hot = leads.filter(l => hotScore(l) >= 3 && !["closed", "deleted"].includes(l.status)).length;
    const demosToday = leads.filter(l => l.status === "demo_booked").length;
    const trialsEnding = leads.filter(l => l.status === "trial").length;
    return { overdue, hot, demosToday, trialsEnding };
  }, [leads]);

  // Conversion per channel (acquisition source)
  const sourceStats = useMemo(() => {
    const buckets = {};
    for (const l of leads) {
      const src = l.source_page || "outbound_manual";
      if (!buckets[src]) buckets[src] = { total: 0, closed: 0, demos: 0 };
      buckets[src].total += 1;
      if (l.status === "closed") buckets[src].closed += 1;
      if (["demo_booked", "trial", "closed"].includes(l.status)) buckets[src].demos += 1;
    }
    return Object.entries(buckets).map(([key, v]) => {
      const meta = SOURCES.find(s => s.value === key);
      return {
        key,
        label: meta?.label || key,
        icon: meta?.icon,
        total: v.total,
        closed: v.closed,
        demos: v.demos,
        conversion: v.total > 0 ? Math.round((v.closed / v.total) * 100) : 0,
      };
    }).sort((a, b) => b.total - a.total);
  }, [leads]);

  const monthName = new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const commission = stats?.total_commission_earned || 0;

  // ------------ Render ------------
  if (loading && !leads.length) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 pb-24 md:pb-8" data-testid="sales-dashboard-v2">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ============ HEADER ============ */}
        <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="sales-header">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              Dashboard Sales — {user?.name || ""}
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              <span className="capitalize">{monthName}</span> · {language === "it" ? "Buon lavoro" : "Keep pushing"} 🚀
            </p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell onOpenLead={(leadId) => {
              const l = leads.find(x => x.lead_id === leadId);
              if (l) setDetailLead(l);
              else toast.error("Lead non più visibile");
            }} />
            <Button
              onClick={() => setNewLeadOpen(true)}
              className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg"
              data-testid="new-lead-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> {language === "it" ? "Nuovo Lead" : "New Lead"}
            </Button>
          </div>
        </div>

        {/* ============ KPI GRID ============ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sales-kpi-grid">
          <KpiCard icon={Plus} label={language === "it" ? "Lead Inseriti" : "Leads"} value={kpis.inserted} color="from-blue-500 to-cyan-600" testId="kpi-inserted" />
          <KpiCard icon={PhoneCall} label="Contattati" value={kpis.contacted} color="from-violet-500 to-purple-600" testId="kpi-contacted" />
          <KpiCard icon={Flame} label="Interessati" value={kpis.interested} color="from-rose-500 to-pink-600" testId="kpi-interested" />
          <KpiCard icon={Calendar} label="Demo" value={kpis.demos} color="from-amber-500 to-orange-600" testId="kpi-demos" />
          <KpiCard icon={Check} label="Chiusi" value={kpis.closed} color="from-emerald-500 to-teal-600" testId="kpi-closed" />
          <KpiCard icon={Wallet} label={language === "it" ? "Commissioni" : "Commission"} value={`€${Number(commission).toFixed(0)}`} color="from-slate-700 to-slate-900" testId="kpi-commission" />
        </div>

        {/* ============ CONVERSION PER CHANNEL ============ */}
        <ChannelConversionCard stats={sourceStats} />

        {/* ============ ACTION CENTER ============ */}
        <div className="rounded-3xl p-5 sm:p-6 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-rose-950/30 border border-amber-200/60 dark:border-amber-800/40" data-testid="action-center">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-base sm:text-lg">{language === "it" ? "Oggi devi fare" : "Today's focus"}</h2>
              <p className="text-xs text-muted-foreground">{language === "it" ? "Le azioni più importanti per chiudere." : "What matters most to close."}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <ActionBtn count={actionCenter.overdue} label={language === "it" ? "follow-up in ritardo" : "overdue follow-ups"} icon={Clock} color="from-red-500 to-rose-600" onClick={() => setSectorFilter("all")} testId="action-overdue" />
            <ActionBtn count={actionCenter.demosToday} label={language === "it" ? "demo pianificate" : "demos planned"} icon={Calendar} color="from-violet-500 to-purple-600" testId="action-demos" />
            <ActionBtn count={actionCenter.hot} label={language === "it" ? "lead caldi" : "hot leads"} icon={Flame} color="from-orange-500 to-red-500" testId="action-hot" />
            <ActionBtn count={actionCenter.trialsEnding} label={language === "it" ? "trial in chiusura" : "trials closing"} icon={AlertCircle} color="from-amber-500 to-orange-500" testId="action-trials" />
          </div>
        </div>

        {/* ============ SEARCH + FILTERS ============ */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="search-bar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={language === "it" ? "Cerca attività, referente, città…" : "Search…"}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-full"
              data-testid="sales-search-input"
            />
          </div>
          <Select value={sectorFilter} onValueChange={setSectorFilter}>
            <SelectTrigger className="w-[170px] rounded-full" data-testid="sales-sector-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === "it" ? "Tutti i settori" : "All sectors"}</SelectItem>
              {SECTORS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* ============ PIPELINE KANBAN ============ */}
        <div className="rounded-3xl bg-card border p-4" data-testid="pipeline-kanban">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-sm sm:text-base flex items-center gap-2">
              <Rocket className="w-4 h-4 text-cyan-500" /> Pipeline
              {isDesktop && (
                <span className="hidden lg:inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-600 dark:text-violet-300 border border-violet-500/20">
                  <GripVertical className="w-3 h-3" /> Trascina per spostare
                </span>
              )}
            </h2>
            <span className="text-xs text-muted-foreground">{filteredLeads.length} lead</span>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => { setActiveDragLead(null); setHoverStage(null); }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 overflow-x-auto">
              {STAGES.map(stage => (
                <KanbanColumn
                  key={stage.key}
                  stage={stage}
                  leads={byStage[stage.key] || []}
                  isHover={hoverStage === stage.key}
                  isDesktop={isDesktop}
                  onMove={moveStage}
                  onOpen={(l) => setDetailLead(l)}
                  onReactivate={reactivateLead}
                  movingLeadId={movingLeadId}
                  stages={STAGES}
                />
              ))}
            </div>
            <DragOverlay dropAnimation={{ duration: 200, easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)" }}>
              {activeDragLead ? (
                <div className="rotate-2 scale-105 shadow-2xl shadow-violet-500/40 ring-2 ring-violet-500/60 rounded-xl">
                  <LeadCardInner lead={activeDragLead} dragging />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* ============ NOT INTERESTED RECOVERY PANEL ============ */}
        <NotInterestedPanel stats={niStats} />

        {/* ============ COMMISSION PANEL ============ */}
        <div className="rounded-3xl p-5 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 text-white border border-slate-700" data-testid="commission-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">{language === "it" ? "Le tue commissioni" : "Your commissions"}</p>
              <p className="text-3xl font-bold mt-1 bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                €{Number(commission).toFixed(2)}
              </p>
              <p className="text-xs text-slate-400 mt-1">{kpis.closed} {language === "it" ? "clienti chiusi questo mese" : "clients closed this month"}</p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <div className="text-right">
                <p className="text-slate-400">{language === "it" ? "Tasso" : "Rate"}</p>
                <p className="font-semibold">{stats?.commission_rate || 0}{stats?.commission_type === "percentage" ? "%" : "€"}</p>
              </div>
              <div className="text-right">
                <p className="text-slate-400">MRR</p>
                <p className="font-semibold">€{(kpis.closed * 179).toFixed(0)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ============ MOBILE STICKY "NEW LEAD" ============ */}
      <div className="md:hidden fixed bottom-4 right-4 z-30">
        <Button
          onClick={() => setNewLeadOpen(true)}
          className="h-14 w-14 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 shadow-2xl shadow-emerald-500/40"
          data-testid="mobile-new-lead-btn"
        >
          <Plus className="w-6 h-6" />
        </Button>
      </div>

      {/* ============ DIALOGS ============ */}
      <NewLeadDialog open={newLeadOpen} onOpenChange={setNewLeadOpen} form={form} setForm={setForm} onSave={createLead} saving={saving || checkingDup} />
      <DuplicateModal
        open={dupOpen}
        onOpenChange={(o) => { setDupOpen(o); if (!o) setDupCheck(null); }}
        result={dupCheck}
        isSuperAdmin={isSuperAdmin}
        onOpenLead={(lead) => { setDupOpen(false); setDetailLead(lead); }}
        onRequestReassign={requestReassign}
        onForceCreate={() => submitCreate({ force: true })}
        onCancelCreate={() => { setDupOpen(false); setDupCheck(null); }}
        saving={saving}
      />
      <NotInterestedDialog
        ctx={notInterestedDialog}
        onClose={() => setNotInterestedDialog(null)}
        onSubmit={submitNotInterested}
        moving={!!movingLeadId}
      />
      <ClosedWonDialog
        ctx={closedWonDialog}
        onClose={() => setClosedWonDialog(null)}
        onSubmit={submitClosedWon}
        saving={!!movingLeadId}
      />
      <LeadDetailDialog lead={detailLead} onClose={() => setDetailLead(null)} onRefresh={fetch} onMove={moveStage} onReactivate={reactivateLead} />
    </div>
  );
};

// ============================================================
// KPI Card
// ============================================================
const KpiCard = ({ icon: Icon, label, value, color, testId }) => (
  <div className={`p-3 rounded-2xl bg-gradient-to-br ${color} text-white shadow-lg`} data-testid={testId}>
    <Icon className="w-4 h-4 opacity-80 mb-1" />
    <p className="text-[10px] uppercase tracking-wider opacity-90">{label}</p>
    <p className="text-xl sm:text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

// ============================================================
// Action Button (in Action Center)
// ============================================================
const ActionBtn = ({ count, label, icon: Icon, color, testId }) => (
  <div className={`rounded-2xl p-3 bg-white/60 dark:bg-slate-900/50 border border-white/40 dark:border-slate-700/40 flex items-center gap-3`} data-testid={testId}>
    <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center text-white flex-shrink-0`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <p className="text-lg font-bold leading-none">{count}</p>
      <p className="text-[10px] text-muted-foreground leading-tight">{label}</p>
    </div>
  </div>
);

// ============================================================
// Kanban Column (droppable, highlight on hover)
// ============================================================
const KanbanColumn = ({ stage, leads, isHover, isDesktop, onMove, onOpen, onReactivate, movingLeadId, stages }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });
  const active = isHover || isOver;
  return (
    <div
      ref={setNodeRef}
      className={`rounded-2xl p-3 ${stage.light} border transition-all duration-200 ${
        active ? "ring-2 ring-violet-500/60 border-violet-400 scale-[1.015] shadow-lg shadow-violet-500/20" : "border"
      }`}
      data-testid={`pipeline-col-${stage.key}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${stage.color} ${active ? "animate-pulse" : ""}`} />
          <span className="text-xs font-semibold">{stage.label}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full bg-background border transition-colors ${active ? "border-violet-400 text-violet-600 dark:text-violet-300" : ""}`}>
          {leads.length}
        </span>
      </div>
      <div className="space-y-2 min-h-[120px]">
        {leads.map(l => (
          <LeadCard
            key={l.lead_id}
            lead={l}
            stages={stages}
            onMove={onMove}
            onOpen={() => onOpen(l)}
            onReactivate={onReactivate}
            moving={movingLeadId === l.lead_id}
            draggable={isDesktop}
          />
        ))}
        {leads.length === 0 && (
          <p className={`text-[11px] text-center py-4 transition-colors ${active ? "text-violet-500 font-medium" : "text-muted-foreground"}`}>
            {active ? "Rilascia qui" : (stage.parking ? "Nessun lead parcheggiato" : "Vuoto")}
          </p>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Lead Card — wrapper (drag on desktop) + inner visual
// ============================================================
const LeadCard = ({ lead, stages, onMove, onOpen, onReactivate, moving, draggable }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: lead.lead_id,
    disabled: !draggable,
  });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0 : 1 }
    : undefined;
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...(draggable ? listeners : {})}>
      <LeadCardInner
        lead={lead}
        stages={stages}
        onMove={onMove}
        onOpen={onOpen}
        onReactivate={onReactivate}
        moving={moving}
        draggable={draggable}
      />
    </div>
  );
};

const LeadCardInner = ({ lead, stages, onMove, onOpen, onReactivate, moving, draggable, dragging }) => {
  const isHot = hotScore(lead) >= 3;
  const stale = staleClass(lead);
  const isParked = lead.status === "not_interested";
  const isWon = lead.status === "closed_won";
  const reasonInfo = isParked ? NOT_INTERESTED_REASON_MAP[lead.not_interested_reason] : null;
  // Skip parking + terminal stages as nextStage candidate
  const visibleStages = stages ? stages.filter(s => !s.parking && !s.terminal) : [];
  const currentIdx = visibleStages.findIndex(s => s.key === lead.status);
  const nextStage = currentIdx >= 0 && currentIdx < visibleStages.length - 1 ? visibleStages[currentIdx + 1] : null;
  return (
    <div
      className={`p-3 rounded-xl ${isParked ? "bg-zinc-50/80 dark:bg-zinc-900/60 border-zinc-200 dark:border-zinc-700" : isWon ? "bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-800/60" : "bg-background"} border-2 ${isParked || isWon ? "" : stale} ${draggable ? "hover:shadow-lg hover:shadow-violet-500/10 hover:-translate-y-0.5" : "hover:shadow-md"} transition-all relative ${dragging ? "cursor-grabbing" : "cursor-pointer"}`}
      data-testid={dragging ? `lead-card-overlay-${lead.lead_id}` : `lead-card-${lead.lead_id}`}
      onClick={(e) => {
        // Ignore if interacting with inner interactive element
        if (e.defaultPrevented) return;
        const tag = (e.target.tagName || "").toUpperCase();
        if (["A", "BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(tag)) return;
        if (e.target.closest && e.target.closest("a,button,input,select,textarea")) return;
        onOpen && onOpen();
      }}
    >
      {isHot && !isParked && !isWon && (
        <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-bold flex items-center gap-0.5 shadow">
          <Flame className="w-2.5 h-2.5" /> HOT
        </span>
      )}
      {isWon && (
        <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-[9px] font-bold flex items-center gap-0.5 shadow" data-testid={`lead-won-${lead.lead_id}`}>
          <Trophy className="w-2.5 h-2.5" /> WON
        </span>
      )}
      {isParked && (
        <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 rounded-full bg-zinc-700 text-white text-[9px] font-bold flex items-center gap-0.5 shadow" data-testid={`lead-parked-${lead.lead_id}`}>
          <PauseCircle className="w-2.5 h-2.5" /> PAUSA
        </span>
      )}
      {draggable && (
        <span className="absolute top-1.5 left-1.5 text-muted-foreground/40 hover:text-violet-500 transition-colors pointer-events-none">
          <GripVertical className="w-3 h-3" />
        </span>
      )}
      <div className={`${draggable ? "pl-4" : ""} flex items-start justify-between gap-1`}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen && onOpen(); }}
          onPointerDown={(e) => e.stopPropagation()}
          className="font-semibold text-sm text-foreground truncate text-left hover:text-violet-600 transition-colors"
          data-testid={`lead-open-${lead.lead_id}`}
        >
          {lead.name}
        </button>
      </div>
      <div className={`${draggable ? "pl-4" : ""} flex items-center gap-1.5 text-[10px] text-muted-foreground mt-0.5 flex-wrap`}>
        {lead.sector && <span className="capitalize">{lead.sector}</span>}
        {lead.city && <><span>·</span><span>{lead.city}</span></>}
      </div>
      {/* Not-interested reason pill */}
      {isParked && reasonInfo && (
        <div className={`${draggable ? "pl-4" : ""} mt-1.5`}>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-zinc-200/70 dark:bg-zinc-800/80 text-zinc-700 dark:text-zinc-200" data-testid={`lead-reason-${lead.lead_id}`}>
            <span>{reasonInfo.emoji}</span>
            {reasonInfo.label}
          </span>
          {lead.recall_at && (
            <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300">
              <Bell className="w-2.5 h-2.5" />
              {new Date(lead.recall_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
            </span>
          )}
        </div>
      )}
      {/* Won revenue badge */}
      {isWon && (lead.first_year_revenue || lead.estimated_value) && (
        <div className={`${draggable ? "pl-4" : ""} mt-1.5`}>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-sm">
            <Euro className="w-2.5 h-2.5" />
            {(lead.first_year_revenue || lead.estimated_value).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
            {lead.plan_sold && <span className="opacity-90">· {lead.plan_sold}</span>}
          </span>
        </div>
      )}
      {(lead.instagram_username || lead.phone) && (
        <div className={`${draggable ? "pl-4" : ""} flex items-center gap-1.5 mt-1.5 text-[10px]`}>
          {lead.instagram_username && (
            <a href={`https://instagram.com/${lead.instagram_username}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-pink-500 hover:underline">
              <Instagram className="w-3 h-3" />@{lead.instagram_username}
            </a>
          )}
          {lead.phone && (
            <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} className="inline-flex items-center gap-0.5 text-emerald-500 hover:underline">
              <MessageSquare className="w-3 h-3" />WA
            </a>
          )}
        </div>
      )}
      {/* Reactivate CTA on parked cards */}
      {isParked && onReactivate && (
        <button
          onClick={(e) => { e.stopPropagation(); onReactivate(lead.lead_id); }}
          onPointerDown={(e) => e.stopPropagation()}
          disabled={moving}
          className="mt-2 w-full text-[10px] font-semibold py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white inline-flex items-center justify-center gap-1 transition-all shadow-sm hover:shadow"
          data-testid={`lead-reactivate-${lead.lead_id}`}
        >
          {moving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <><RotateCcw className="w-2.5 h-2.5" /> Riattiva lead</>}
        </button>
      )}
      {/* Mobile-only quick advance button (desktop uses drag) — skip for parked/won */}
      {!isParked && !isWon && nextStage && !draggable && onMove && (
        <button
          onClick={(e) => { e.stopPropagation(); onMove(lead.lead_id, nextStage.key, { via: "button" }); }}
          disabled={moving}
          className="mt-2 w-full text-[10px] py-1 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground inline-flex items-center justify-center gap-0.5 transition-colors"
          data-testid={`lead-advance-${lead.lead_id}`}
        >
          {moving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <>→ {nextStage.label}</>}
        </button>
      )}
    </div>
  );
};

// ============================================================
// Plan catalog used by Closed Won dialog (mirrors backend)
// ============================================================
const PLAN_CATALOG = [
  { key: "essential", label: "Starter", monthly: 34.95, annual: 349 },
  { key: "growth", label: "Pro", monthly: 89, annual: 890 },
  { key: "pro", label: "Premium", monthly: 179, annual: 1790 },
  { key: "dental_pro", label: "Dental Pro", monthly: 179, annual: 1790 },
  { key: "custom", label: "Custom", monthly: 0, annual: 0 },
];

// ============================================================
// Closed Won Dialog — Premium Deal Capture
// ============================================================
const ClosedWonDialog = ({ ctx, onClose, onSubmit, saving }) => {
  const [planSold, setPlanSold] = useState("");
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [discount, setDiscount] = useState("");
  const [discountType, setDiscountType] = useState("amount");
  const [setupFee, setSetupFee] = useState("");
  const [activationDate, setActivationDate] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  useEffect(() => {
    if (ctx) {
      setPlanSold(""); setBillingCycle("monthly");
      setDiscount(""); setDiscountType("amount");
      setSetupFee(""); setActivationDate(new Date().toISOString().slice(0, 10));
      setCustomAmount("");
    }
  }, [ctx]);

  if (!ctx) return null;

  const plan = PLAN_CATALOG.find(p => p.key === planSold);
  const isCustom = planSold === "custom";
  const baseAmount = isCustom ? parseFloat(customAmount) || 0 : (plan ? (billingCycle === "annual" ? plan.annual : plan.monthly) : 0);
  const discountValue = parseFloat(discount) || 0;
  const setupValue = parseFloat(setupFee) || 0;
  const discountedBase = discountType === "percent"
    ? Math.max(0, baseAmount * (1 - Math.min(discountValue, 100) / 100))
    : Math.max(0, baseAmount - discountValue);
  const firstYearRevenue = (billingCycle === "annual" ? discountedBase : discountedBase * 12) + setupValue;
  const mrrValue = billingCycle === "annual" ? discountedBase / 12 : discountedBase;

  const canSave = !!planSold && !!billingCycle && (!isCustom || (parseFloat(customAmount) > 0));

  const handleSubmit = () => {
    onSubmit({
      plan_sold: planSold,
      billing_cycle: billingCycle,
      discount: discountValue || 0,
      discount_type: discountType,
      setup_fee: setupValue || 0,
      activation_date: activationDate ? new Date(activationDate).toISOString() : null,
      ...(isCustom ? { custom_amount: parseFloat(customAmount) || 0 } : {}),
      _estimated_revenue: firstYearRevenue,
    });
  };

  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl p-0 overflow-hidden gap-0" data-testid="closed-won-dialog">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-500 via-teal-600 to-cyan-700 text-white p-5">
          <div className="absolute inset-0 opacity-15" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(255,255,255,.6) 0%, transparent 50%)" }} />
          <div className="relative flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 shadow-lg">
              <Trophy className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold leading-tight">🎉 Deal Chiuso!</DialogTitle>
                <p className="text-xs opacity-90 mt-0.5 truncate">{ctx.leadName}</p>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Plans */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Piano venduto</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {PLAN_CATALOG.map(p => (
                <button
                  key={p.key}
                  onClick={() => setPlanSold(p.key)}
                  className={`p-2.5 rounded-xl border-2 text-left transition-all ${planSold === p.key ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 shadow-sm" : "border-muted hover:border-muted-foreground/30"}`}
                  data-testid={`cw-plan-${p.key}`}
                >
                  <p className="text-[10px] uppercase tracking-wider opacity-70">{p.key === "essential" ? "Base" : p.key === "custom" ? "Custom" : ""}</p>
                  <p className="font-bold text-sm">{p.label}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {p.monthly > 0 ? `€${p.monthly}/mo` : "Su misura"}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Billing cycle */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Billing</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`p-3 rounded-xl border-2 transition-all ${billingCycle === "monthly" ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30 shadow-sm" : "border-muted hover:border-muted-foreground/30"}`}
                data-testid="cw-billing-monthly"
              >
                <p className="font-semibold text-sm">Mensile</p>
                <p className="text-[10px] text-muted-foreground">{plan && !isCustom ? `€${plan.monthly}/mese` : "—"}</p>
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`p-3 rounded-xl border-2 transition-all ${billingCycle === "annual" ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30 shadow-sm" : "border-muted hover:border-muted-foreground/30"}`}
                data-testid="cw-billing-annual"
              >
                <p className="font-semibold text-sm">Annuale <span className="text-[9px] text-emerald-500 font-bold">2 mesi gratis</span></p>
                <p className="text-[10px] text-muted-foreground">{plan && !isCustom ? `€${plan.annual}/anno` : "—"}</p>
              </button>
            </div>
          </div>

          {/* Custom amount */}
          {isCustom && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Importo {billingCycle === "annual" ? "annuale" : "mensile"}</p>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="0.00"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="cw-custom-amount"
                />
              </div>
            </div>
          )}

          {/* Discount + Setup fee */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Sconto</p>
              <div className="flex gap-1">
                <Input
                  type="number"
                  placeholder="0"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  className="h-10 flex-1"
                  data-testid="cw-discount"
                />
                <Select value={discountType} onValueChange={setDiscountType}>
                  <SelectTrigger className="w-[70px] h-10" data-testid="cw-discount-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount">€</SelectItem>
                    <SelectItem value="percent">%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Setup fee</p>
              <div className="relative">
                <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="number"
                  placeholder="0"
                  value={setupFee}
                  onChange={(e) => setSetupFee(e.target.value)}
                  className="pl-9 h-10"
                  data-testid="cw-setup-fee"
                />
              </div>
            </div>
          </div>

          {/* Activation date */}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Data attivazione</p>
            <Input
              type="date"
              value={activationDate}
              onChange={(e) => setActivationDate(e.target.value)}
              className="h-10"
              data-testid="cw-activation-date"
            />
          </div>

          {/* Live revenue preview */}
          {planSold && (baseAmount > 0 || isCustom) && (
            <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-950/40 dark:via-teal-950/40 dark:to-cyan-950/40 border border-emerald-200 dark:border-emerald-800/50" data-testid="cw-revenue-preview">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 opacity-80">MRR</p>
                  <p className="font-bold text-sm" data-testid="cw-preview-mrr">€{mrrValue.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 opacity-80">Anno 1</p>
                  <p className="font-bold text-base text-emerald-700 dark:text-emerald-300" data-testid="cw-preview-revenue">€{firstYearRevenue.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 opacity-80">Sconto</p>
                  <p className="font-bold text-sm">{discountValue > 0 ? (discountType === "percent" ? `-${discountValue}%` : `-€${discountValue}`) : "—"}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-muted/20 p-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="cw-cancel">Annulla</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSave || saving}
            className="rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow"
            data-testid="cw-confirm"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Trophy className="w-3.5 h-3.5 mr-1.5" />}
            Conferma deal
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Not-Interested Reason Dialog — premium, blocking
// ============================================================
const NotInterestedDialog = ({ ctx, onClose, onSubmit, moving }) => {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [recallAt, setRecallAt] = useState("");
  useEffect(() => {
    if (ctx) { setReason(""); setNote(""); setRecallAt(""); }
  }, [ctx]);
  if (!ctx) return null;
  const needsRecall = reason === "recall_future";
  const canSave = !!reason && (!needsRecall || !!recallAt);
  return (
    <Dialog open={!!ctx} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid="not-interested-dialog">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-zinc-700 via-zinc-800 to-zinc-900 text-white p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <PauseCircle className="w-5 h-5" />
            </div>
            <div>
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">Sposta in pausa</DialogTitle>
                <p className="text-xs opacity-80 mt-0.5">Indica il motivo per recuperarlo al momento giusto.</p>
              </DialogHeader>
            </div>
          </div>
        </div>
        {/* Body */}
        <div className="p-5 space-y-3">
          <div className="space-y-2">
            {NOT_INTERESTED_REASONS.map(r => (
              <button
                key={r.key}
                onClick={() => setReason(r.key)}
                className={`w-full p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3 ${
                  reason === r.key
                    ? "border-violet-500 bg-violet-50 dark:bg-violet-950/30 shadow-sm"
                    : "border-muted hover:border-muted-foreground/30 hover:bg-muted/30"
                }`}
                data-testid={`ni-reason-${r.key}`}
              >
                <span className="text-xl">{r.emoji}</span>
                <span className="flex-1 text-sm font-medium">{r.label}</span>
                {reason === r.key && <Check className="w-4 h-4 text-violet-500" />}
              </button>
            ))}
          </div>
          {needsRecall && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 space-y-2" data-testid="ni-recall-panel">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                <Bell className="w-3.5 h-3.5" /> Quando vuoi essere ricontattato?
              </p>
              <Input
                type="datetime-local"
                value={recallAt}
                onChange={(e) => setRecallAt(e.target.value)}
                className="h-9 text-sm"
                min={new Date().toISOString().slice(0, 16)}
                data-testid="ni-recall-datetime"
              />
              <p className="text-[10px] text-amber-600 dark:text-amber-400">
                Verrà creato automaticamente un reminder collegato.
              </p>
            </div>
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota (opzionale): cosa ha detto il prospect?"
            rows={2}
            className="w-full text-sm rounded-lg border p-2.5 resize-none focus:outline-none focus:ring-2 focus:ring-violet-500/40"
            data-testid="ni-note"
          />
        </div>
        {/* Footer */}
        <div className="border-t bg-muted/20 p-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="ni-cancel">Annulla</Button>
          <Button
            onClick={() => onSubmit({ reason, note, recallAt: recallAt ? new Date(recallAt).toISOString() : null })}
            disabled={!canSave || moving}
            className="rounded-full bg-zinc-700 hover:bg-zinc-800 text-white"
            data-testid="ni-save"
          >
            {moving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <PauseCircle className="w-3.5 h-3.5 mr-1.5" />}
            Conferma
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// Not-Interested Recovery Panel — analytics + reactivation insight
// ============================================================
const NotInterestedPanel = ({ stats }) => {
  if (!stats) return null;
  const { total_not_interested = 0, current_in_stage = 0, total_recovered = 0, recovery_rate = 0, by_reason = [] } = stats;
  if (total_not_interested === 0 && current_in_stage === 0) return null;
  const top3 = by_reason.slice(0, 3);
  return (
    <div className="rounded-3xl p-5 sm:p-6 bg-gradient-to-br from-zinc-50 via-slate-50 to-zinc-100 dark:from-zinc-900/60 dark:via-slate-900/60 dark:to-zinc-900/60 border border-zinc-200 dark:border-zinc-800" data-testid="not-interested-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-zinc-700 to-slate-800 flex items-center justify-center flex-shrink-0 shadow-lg">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-bold text-base sm:text-lg flex items-center gap-2">
            Pipeline recuperabili
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-300 border border-emerald-500/20">
              {recovery_rate}% recovery
            </span>
          </h2>
          <p className="text-xs text-muted-foreground">Non sono persi: hanno solo bisogno di tempo o un'angolazione diversa.</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <MiniStat label="In pausa ora" value={current_in_stage} color="from-zinc-600 to-slate-700" testId="ni-stat-current" />
        <MiniStat label="Totale 30gg" value={total_not_interested} color="from-slate-500 to-zinc-600" testId="ni-stat-total" />
        <MiniStat label="Riattivati" value={total_recovered} color="from-emerald-500 to-teal-600" testId="ni-stat-recovered" />
        <MiniStat label="Recovery rate" value={`${recovery_rate}%`} color="from-violet-500 to-purple-600" testId="ni-stat-rate" />
      </div>
      {top3.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Motivi principali</p>
          {top3.map((r) => {
            const info = NOT_INTERESTED_REASON_MAP[r.reason] || { emoji: "💬", label: r.label };
            return (
              <div key={r.reason} className="flex items-center gap-3" data-testid={`ni-reason-row-${r.reason}`}>
                <span className="text-base flex-shrink-0">{info.emoji}</span>
                <span className="text-xs font-medium flex-1 truncate">{info.label}</span>
                <div className="flex-1 max-w-[160px] h-2 rounded-full bg-zinc-200 dark:bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-zinc-500 to-slate-700 transition-all"
                    style={{ width: `${Math.max(4, r.pct)}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-muted-foreground w-12 text-right">{r.count} · {r.pct}%</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MiniStat = ({ label, value, color, testId }) => (
  <div className={`p-3 rounded-2xl bg-gradient-to-br ${color} text-white shadow`} data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wider opacity-90">{label}</p>
    <p className="text-xl sm:text-2xl font-bold mt-0.5">{value}</p>
  </div>
);

// ============================================================
// New Lead Dialog (speed optimized, 15s input)
// ============================================================
const NewLeadDialog = ({ open, onOpenChange, form, setForm, onSave, saving }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-emerald-500" /> Nuovo Lead</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 mt-3">
        <Input placeholder="Nome attività *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus className="rounded-lg" data-testid="newlead-name" />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Referente" value={form.referent_name} onChange={(e) => setForm({ ...form, referent_name: e.target.value })} className="rounded-lg" data-testid="newlead-referent" />
          <Input placeholder="Città" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="rounded-lg" data-testid="newlead-city" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Instagram @" value={form.instagram_username} onChange={(e) => setForm({ ...form, instagram_username: e.target.value })} className="rounded-lg" data-testid="newlead-ig" />
          <Input placeholder="WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-lg" data-testid="newlead-phone" />
        </div>
        <Input
          type="email"
          placeholder="Email (opzionale)"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="rounded-lg"
          data-testid="newlead-email"
        />
        <div className="grid grid-cols-3 gap-2">
          <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
            <SelectTrigger className="rounded-lg" data-testid="newlead-sector"><SelectValue /></SelectTrigger>
            <SelectContent>{SECTORS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.interest_level} onValueChange={(v) => setForm({ ...form, interest_level: v })}>
            <SelectTrigger className="rounded-lg" data-testid="newlead-interest"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="basso">Basso 🔵</SelectItem>
              <SelectItem value="medio">Medio 🟡</SelectItem>
              <SelectItem value="alto">Alto 🔥</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
            <SelectTrigger className="rounded-lg" data-testid="newlead-source"><SelectValue /></SelectTrigger>
            <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <textarea
          placeholder="Note (opzionale)"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          rows={2}
          className="w-full rounded-lg border p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          data-testid="newlead-notes"
        />
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full">Annulla</Button>
          <Button onClick={onSave} disabled={saving || !form.name.trim()} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="newlead-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Salva Lead
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

// ============================================================
// Duplicate Protection Modal — Premium SaaS
// ============================================================
const SEVERITY_THEME = {
  block: {
    label: "BLOCCATO",
    sublabel: "Lead duplicato — match certo",
    headerBg: "from-red-500 via-rose-500 to-red-600",
    badgeBg: "bg-red-500 text-white",
    accentRing: "ring-red-500/30",
    iconBg: "bg-red-100 dark:bg-red-950/40 text-red-600 dark:text-red-300",
    icon: AlertCircle,
    cardBorder: "border-red-300 dark:border-red-800",
    glowShadow: "shadow-red-500/20",
  },
  warning: {
    label: "ATTENZIONE",
    sublabel: "Possibile duplicato — verifica",
    headerBg: "from-amber-500 via-orange-500 to-amber-600",
    badgeBg: "bg-amber-500 text-white",
    accentRing: "ring-amber-500/30",
    iconBg: "bg-amber-100 dark:bg-amber-950/40 text-amber-600 dark:text-amber-300",
    icon: AlertCircle,
    cardBorder: "border-amber-300 dark:border-amber-800",
    glowShadow: "shadow-amber-500/20",
  },
  soft: {
    label: "INFO",
    sublabel: "Match leggero — controllo consigliato",
    headerBg: "from-sky-500 via-blue-500 to-cyan-600",
    badgeBg: "bg-sky-500 text-white",
    accentRing: "ring-sky-500/30",
    iconBg: "bg-sky-100 dark:bg-sky-950/40 text-sky-600 dark:text-sky-300",
    icon: Sparkles,
    cardBorder: "border-sky-300 dark:border-sky-800",
    glowShadow: "shadow-sky-500/20",
  },
};

const REASON_LABEL = {
  phone: { label: "Telefono identico", icon: Phone },
  email: { label: "Email identica", icon: FileText },
  instagram: { label: "Instagram identico", icon: Instagram },
  name: { label: "Nome simile", icon: Search },
};

const STATUS_LABEL = {
  new: "Nuovo",
  contacted: "Contattato",
  interested: "Interessato",
  demo_booked: "Demo Fissata",
  trial: "Trial",
  closed_won: "Chiuso ✓",
  closed: "Chiuso ✓",
  lost: "Perso",
  recall_later: "Da risentire più avanti",
  no_reply: "Mai Risposto",
  not_interested: "Non Interessato",
};

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days <= 0) return "oggi";
  if (days === 1) return "1 giorno fa";
  if (days < 30) return `${days} giorni fa`;
  if (days < 365) return `${Math.floor(days / 30)} mesi fa`;
  return `${Math.floor(days / 365)} anni fa`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
};

const DuplicateModal = ({ open, onOpenChange, result, isSuperAdmin, onOpenLead, onRequestReassign, onForceCreate, onCancelCreate, saving }) => {
  if (!result) return null;
  const sev = result.severity || "soft";
  const theme = SEVERITY_THEME[sev] || SEVERITY_THEME.soft;
  const HeaderIcon = theme.icon;
  const dups = result.duplicates || [];
  const canForce = isSuperAdmin && (result.can_force ?? true);
  const canForceForSales = sev !== "block" && !isSuperAdmin; // sales can force on warning/soft

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden gap-0" data-testid="dup-modal">
        {/* HEADER */}
        <div className={`relative bg-gradient-to-br ${theme.headerBg} text-white p-6 pb-5`}>
          <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, rgba(255,255,255,.4) 0%, transparent 50%)" }} />
          <div className="relative flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0 shadow-lg">
              <HeaderIcon className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold tracking-widest px-2 py-0.5 rounded-full bg-white/20 backdrop-blur" data-testid="dup-severity-badge">
                    {theme.label}
                  </span>
                  <span className="text-[11px] opacity-90">{dups.length} {dups.length === 1 ? "match" : "match"}</span>
                </div>
                <DialogTitle className="text-xl font-bold leading-tight">
                  {sev === "block" && (isSuperAdmin ? "Lead duplicato — puoi forzare" : "Lead già presente nel CRM")}
                  {sev === "warning" && "Possibile duplicato"}
                  {sev === "soft" && "Match leggero rilevato"}
                </DialogTitle>
                <p className="text-sm opacity-90 mt-1">{theme.sublabel}</p>
              </DialogHeader>
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="p-6 space-y-3 max-h-[55vh] overflow-y-auto bg-card">
          {dups.map((d) => {
            const reason = REASON_LABEL[d._match_reason] || REASON_LABEL.name;
            const ReasonIcon = reason.icon;
            const status = STATUS_LABEL[d.status] || d.status;
            const stageColor = STAGES.find(s => s.key === d.status)?.color || "bg-slate-500";
            const score = Math.round((d._match_score || 0) * 100);
            return (
              <div
                key={d.lead_id}
                className={`p-4 rounded-2xl bg-background border-2 ${theme.cardBorder} hover:shadow-lg ${theme.glowShadow} transition-all`}
                data-testid={`dup-card-${d.lead_id}`}
              >
                {/* Top row: name + score + reason */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-base truncate flex items-center gap-2">
                      {d.name}
                      {d.is_mine && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 font-bold">
                          TUO
                        </span>
                      )}
                    </h3>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <ReasonIcon className="w-3 h-3" />
                        {reason.label}
                      </span>
                      <span>·</span>
                      <span className="font-bold">{score}% match</span>
                    </div>
                  </div>
                  <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${theme.badgeBg} flex-shrink-0 shadow`}>
                    {score}%
                  </span>
                </div>

                {/* Owner + stage row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3 mt-3">
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Owner</span>
                    <span className="font-medium truncate">{d.owner_name || "—"}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Stage</span>
                    <span className="inline-flex items-center gap-1 font-medium">
                      <span className={`w-2 h-2 rounded-full ${stageColor}`} />
                      {status}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Ultimo agg.</span>
                    <span className="font-medium">{fmtRelative(d.last_entry_at || d.updated_at || d.created_at)}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] uppercase tracking-wider text-muted-foreground">Creato</span>
                    <span className="font-medium">{fmtDate(d.created_at)}</span>
                  </div>
                </div>

                {/* Contact pills */}
                {(d.phone || d.email || d.instagram_username) && (
                  <div className="flex items-center gap-1.5 flex-wrap mb-3">
                    {d.phone && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{d.phone}</span>}
                    {d.email && <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-950/30 text-violet-700 dark:text-violet-300 truncate max-w-[180px]">{d.email}</span>}
                    {d.instagram_username && <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-50 dark:bg-pink-950/30 text-pink-700 dark:text-pink-300 inline-flex items-center gap-1"><Instagram className="w-2.5 h-2.5" />@{d.instagram_username}</span>}
                  </div>
                )}

                {/* Action buttons per card */}
                <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
                  {d.is_mine ? (
                    <button
                      onClick={() => onOpenLead(d)}
                      className="text-xs font-medium px-3 py-1.5 rounded-full bg-violet-500 hover:bg-violet-600 text-white inline-flex items-center gap-1 transition-colors shadow-sm"
                      data-testid={`dup-open-lead-${d.lead_id}`}
                    >
                      <ExternalLink className="w-3 h-3" /> Apri scheda
                    </button>
                  ) : (
                    <button
                      onClick={() => onRequestReassign(d.lead_id)}
                      className="text-xs font-medium px-3 py-1.5 rounded-full bg-amber-500/15 hover:bg-amber-500/25 text-amber-700 dark:text-amber-300 inline-flex items-center gap-1 transition-colors border border-amber-500/30"
                      data-testid={`dup-reassign-${d.lead_id}`}
                    >
                      <Flag className="w-3 h-3" /> Richiedi riassegnazione
                    </button>
                  )}
                  {isSuperAdmin && !d.is_mine && (
                    <span className="text-[10px] text-muted-foreground italic">
                      Owner: {d.owner_name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FOOTER */}
        <div className="border-t bg-muted/30 p-4 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 max-w-md">
            <Sparkles className="w-3 h-3 flex-shrink-0" />
            {sev === "block" && !isSuperAdmin && "Sales: non puoi creare un duplicato. Apri la scheda esistente o richiedi riassegnazione."}
            {sev === "block" && isSuperAdmin && "Hai privilegi super-admin per forzare la creazione."}
            {sev === "warning" && "Verifica che non sia lo stesso prospect prima di procedere."}
            {sev === "soft" && "Match leggero — probabilmente è un'altra azienda."}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={onCancelCreate}
              className="rounded-full"
              data-testid="dup-cancel"
            >
              Annulla
            </Button>
            {(canForce || canForceForSales) && (
              <Button
                onClick={onForceCreate}
                disabled={saving}
                className={`rounded-full text-white ${
                  sev === "block"
                    ? "bg-red-500 hover:bg-red-600"
                    : sev === "warning"
                    ? "bg-amber-500 hover:bg-amber-600"
                    : "bg-sky-500 hover:bg-sky-600"
                }`}
                data-testid="dup-force-create"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                {isSuperAdmin && sev === "block" ? "Forza creazione (admin)" : "Crea comunque"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// ============================================================
// Notification Bell (admin notes → sales)
// ============================================================
const NotificationBell = ({ onOpenLead }) => {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/sales/my/notifications`, { withCredentials: true });
      setItems(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClickAway = (e) => {
      if (!e.target.closest || !e.target.closest("[data-notif-bell]")) setOpen(false);
    };
    if (open) document.addEventListener("click", onClickAway);
    return () => document.removeEventListener("click", onClickAway);
  }, [open]);

  const handleOpen = async (n) => {
    try {
      await axios.post(`${API}/sales/my/notifications/${n.notification_id}/read`, {}, { withCredentials: true });
    } catch {}
    setOpen(false);
    await load();
    if (onOpenLead && n.lead_id) onOpenLead(n.lead_id);
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/sales/my/notifications/read-all`, {}, { withCredentials: true });
      await load();
    } catch {}
  };

  const fmtRel = (iso) => {
    if (!iso) return "";
    const s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 60) return "ora";
    if (s < 3600) return `${Math.floor(s / 60)}m fa`;
    if (s < 86400) return `${Math.floor(s / 3600)}h fa`;
    return `${Math.floor(s / 86400)}g fa`;
  };

  return (
    <div className="relative" data-notif-bell data-testid="notification-bell">
      <button
        onClick={() => setOpen(v => !v)}
        className="relative h-10 w-10 rounded-full border-2 border-slate-200 dark:border-slate-700 hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950/30 transition-colors flex items-center justify-center"
        data-testid="notification-bell-btn"
        aria-label="Notifiche"
      >
        <Bell className={`w-4 h-4 ${unread > 0 ? "text-rose-600" : "text-slate-500"}`} />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center shadow-sm animate-pulse"
            data-testid="notification-unread-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-[340px] max-w-[calc(100vw-2rem)] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl z-50 overflow-hidden"
          data-testid="notification-popover"
        >
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
            <h3 className="font-bold text-sm flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Notifiche {unread > 0 && <span className="text-xs text-rose-600 font-semibold">({unread})</span>}
            </h3>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-[11px] text-violet-600 hover:text-violet-700 font-medium"
                data-testid="notification-mark-all-read"
              >
                Segna tutte lette
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nessuna notifica
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.notification_id}
                  onClick={() => handleOpen(n)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-violet-50 dark:hover:bg-violet-950/20 transition-colors ${
                    n.read ? "opacity-70" : "bg-rose-50/40 dark:bg-rose-950/20"
                  }`}
                  data-testid={`notification-item-${n.notification_id}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      n.read
                        ? "bg-slate-100 dark:bg-slate-800 text-slate-400"
                        : "bg-gradient-to-br from-rose-500 to-pink-600 text-white shadow-sm"
                    }`}>
                      <Shield className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground truncate">
                        {n.admin_name || "Admin"} · Nota su <span className="text-violet-700 dark:text-violet-300">{n.lead_name || "lead"}</span>
                      </p>
                      <p className="text-[12px] text-muted-foreground line-clamp-2 mt-0.5">{n.preview}</p>
                      <p className="text-[10px] text-muted-foreground mt-1 uppercase tracking-wider">{fmtRel(n.created_at)}</p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-rose-600 mt-2 flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// Conversion per Acquisition Channel (donut + table)
// ============================================================
const CHANNEL_COLORS = {
  instagram: "#ec4899",
  whatsapp: "#10b981",
  facebook: "#3b82f6",
  tiktok: "#0f172a",
  email: "#0ea5e9",
  cold_call: "#f59e0b",
  outbound_manual: "#64748b",
};

const ChannelConversionCard = ({ stats }) => {
  if (!stats || stats.length === 0) {
    return null;
  }
  const totalLeads = stats.reduce((s, x) => s + x.total, 0);
  const totalClosed = stats.reduce((s, x) => s + x.closed, 0);
  const globalConv = totalLeads > 0 ? Math.round((totalClosed / totalLeads) * 100) : 0;

  return (
    <div className="rounded-3xl p-5 sm:p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm" data-testid="channel-conversion-card">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-md">
            <PieChartIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-base sm:text-lg">Conversion per canale</h2>
            <p className="text-xs text-muted-foreground">Quale canale converte di più in clienti chiusi</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Conv. globale</p>
          <p className="text-2xl font-black text-emerald-600">{globalConv}%</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6 items-center">
        {/* Donut chart */}
        <div className="h-56" data-testid="channel-conversion-chart">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={stats}
                dataKey="total"
                nameKey="label"
                cx="50%" cy="50%"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={2}
                label={({ percent }) => percent > 0.08 ? `${Math.round(percent * 100)}%` : ""}
                labelLine={false}
              >
                {stats.map((s) => (
                  <Cell key={s.key} fill={CHANNEL_COLORS[s.key] || "#8b5cf6"} />
                ))}
              </Pie>
              <ReTooltip
                contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #e2e8f0" }}
                formatter={(value, _n, props) => {
                  const d = props.payload;
                  return [`${value} lead · ${d.closed} chiusi (${d.conversion}%)`, d.label];
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Breakdown table */}
        <div className="space-y-1.5" data-testid="channel-conversion-table">
          {stats.map(s => {
            const Icon = s.icon;
            const convColor = s.conversion >= 20 ? "text-emerald-600" : s.conversion >= 10 ? "text-amber-600" : "text-slate-500";
            const pct = totalLeads > 0 ? Math.round((s.total / totalLeads) * 100) : 0;
            return (
              <div key={s.key} className="flex items-center gap-2 text-xs p-1.5 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CHANNEL_COLORS[s.key] || "#8b5cf6" }}
                />
                {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                <span className="font-semibold text-foreground flex-1 truncate">{s.label}</span>
                <span className="text-muted-foreground w-14 text-right">{s.total} lead</span>
                <span className="text-muted-foreground w-10 text-right">{pct}%</span>
                <span className={`font-bold w-12 text-right ${convColor}`} title={`${s.closed}/${s.total} chiusi`}>{s.conversion}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Lead Detail Dialog (quick actions + templates)
// ============================================================
const LeadDetailDialog = ({ lead, onClose, onRefresh, onMove, onReactivate }) => {
  const [entries, setEntries] = useState([]);
  const [entryType, setEntryType] = useState("message");
  const [entryContent, setEntryContent] = useState("");
  const [saving, setSaving] = useState(false);
  // Reminder state
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderAt, setReminderAt] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [savingReminder, setSavingReminder] = useState(false);

  useEffect(() => {
    if (!lead) { setEntries([]); setReminderOpen(false); return; }
    axios.get(`${API}/sales/my/leads/${lead.lead_id}/entries`, { withCredentials: true })
      .then(r => setEntries(r.data.entries || []))
      .catch(() => setEntries([]));
  }, [lead]);

  if (!lead) return null;

  const copyTemplate = (key) => {
    const tpl = TEMPLATES[key];
    const name = lead.referent_name || lead.name;
    const text = tpl.text.replace(/\{\{nome\}\}/g, name);
    navigator.clipboard.writeText(text);
    toast.success("Messaggio copiato negli appunti");
  };

  const addEntry = async () => {
    if (!entryContent.trim()) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/sales/my/leads/${lead.lead_id}/entries`,
        { entry_type: entryType, content: entryContent.trim() },
        { withCredentials: true }
      );
      setEntryContent("");
      const r = await axios.get(`${API}/sales/my/leads/${lead.lead_id}/entries`, { withCredentials: true });
      setEntries(r.data.entries || []);
    } catch { toast.error("Errore"); }
    finally { setSaving(false); }
  };

  const saveReminder = async () => {
    if (!reminderAt) { toast.error("Seleziona data e ora"); return; }
    setSavingReminder(true);
    try {
      const when = new Date(reminderAt).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" });
      const body = `⏰ Reminder per ${when}${reminderNote ? ` — ${reminderNote.trim()}` : ""}`;
      await axios.post(
        `${API}/sales/my/leads/${lead.lead_id}/entries`,
        { entry_type: "followup", content: body },
        { withCredentials: true }
      );
      const r = await axios.get(`${API}/sales/my/leads/${lead.lead_id}/entries`, { withCredentials: true });
      setEntries(r.data.entries || []);
      setReminderAt(""); setReminderNote(""); setReminderOpen(false);
      toast.success("Reminder salvato");
    } catch { toast.error("Errore reminder"); }
    finally { setSavingReminder(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="lead-detail-dialog">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 bg-muted hover:bg-muted/70 transition-colors z-10"
          data-testid="detail-close-btn"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-10">
            {lead.name}
            {hotScore(lead) >= 3 && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500 text-white font-bold flex items-center gap-0.5"><Flame className="w-3 h-3" />HOT</span>}
          </DialogTitle>
          {lead.referent_name && (
            <p className="text-xs text-muted-foreground pr-10">
              Referente: <span className="font-medium text-foreground">{lead.referent_name}</span>
            </p>
          )}
        </DialogHeader>
        <div className="space-y-4 mt-3 max-h-[70vh] overflow-y-auto pr-1">
          {/* Info chips */}
          <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            {lead.sector && <span className="px-2 py-0.5 rounded-full bg-muted capitalize">{lead.sector}</span>}
            {lead.city && <span className="px-2 py-0.5 rounded-full bg-muted">{lead.city}</span>}
            {lead.interest_level && <span className="px-2 py-0.5 rounded-full bg-muted">Interesse {lead.interest_level}</span>}
            {(() => {
              const src = SOURCES.find(s => s.value === (lead.source_page || "outbound_manual"));
              if (!src) return <span className="px-2 py-0.5 rounded-full bg-muted">{lead.source_page || "manual"}</span>;
              const Icon = src.icon;
              return (
                <span className="px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 inline-flex items-center gap-1" data-testid="detail-source-chip">
                  <Icon className="w-3 h-3" />{src.label}
                </span>
              );
            })()}
          </div>

          {/* Contact details */}
          {(lead.phone || lead.email || lead.instagram_username) && (
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 space-y-1.5" data-testid="detail-contacts">
              {lead.phone && (
                <div className="flex items-center gap-2 text-xs">
                  <Phone className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                  <a href={`tel:${lead.phone}`} className="font-medium text-foreground hover:underline">{lead.phone}</a>
                </div>
              )}
              {lead.email && (
                <div className="flex items-center gap-2 text-xs" data-testid="detail-email">
                  <Mail className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                  <a href={`mailto:${lead.email}`} className="font-medium text-foreground hover:underline break-all">{lead.email}</a>
                </div>
              )}
              {lead.instagram_username && (
                <div className="flex items-center gap-2 text-xs">
                  <Instagram className="w-3.5 h-3.5 text-pink-600 flex-shrink-0" />
                  <a href={`https://instagram.com/${lead.instagram_username}`} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground hover:underline">@{lead.instagram_username}</a>
                </div>
              )}
            </div>
          )}

          {/* Initial notes from creation */}
          {lead.notes && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60" data-testid="detail-initial-notes">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700 dark:text-amber-300 mb-1 flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Nota iniziale
              </p>
              <p className="text-xs text-foreground whitespace-pre-wrap leading-relaxed">{lead.notes}</p>
            </div>
          )}

          {/* Quick contacts */}
          <div className="flex flex-wrap gap-2">
            {lead.phone && (
              <a href={`https://wa.me/${lead.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs" data-testid="detail-wa-btn">
                <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
              </a>
            )}
            {lead.instagram_username && (
              <a href={`https://instagram.com/${lead.instagram_username}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white text-xs">
                <Instagram className="w-3.5 h-3.5" /> @{lead.instagram_username}
              </a>
            )}
          </div>

          {/* Stage selector */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-medium text-muted-foreground">Stage</p>
              {lead.status === "not_interested" && onReactivate && (
                <button
                  onClick={() => { onReactivate(lead.lead_id); onClose && onClose(); }}
                  className="text-[11px] font-semibold inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-sm"
                  data-testid="detail-reactivate-btn"
                >
                  <RotateCcw className="w-3 h-3" /> Riattiva lead
                </button>
              )}
            </div>
            {lead.status === "not_interested" && lead.not_interested_reason && (
              <div className="mb-2 p-2.5 rounded-lg bg-zinc-100 dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 text-xs flex items-center gap-2">
                <PauseCircle className="w-3.5 h-3.5 text-zinc-600 dark:text-zinc-300" />
                <span className="font-medium">In pausa:</span>
                <span className="text-muted-foreground">
                  {NOT_INTERESTED_REASON_MAP[lead.not_interested_reason]?.emoji} {NOT_INTERESTED_REASON_MAP[lead.not_interested_reason]?.label || lead.not_interested_reason_label}
                </span>
                {lead.recall_at && (
                  <span className="ml-auto inline-flex items-center gap-1 text-amber-600 dark:text-amber-300">
                    <Bell className="w-3 h-3" /> {new Date(lead.recall_at).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </span>
                )}
              </div>
            )}
            <div className="flex gap-1 flex-wrap">
              {STAGES.map(s => (
                <button
                  key={s.key}
                  onClick={() => onMove(lead.lead_id, s.key)}
                  className={`px-3 py-1 rounded-full text-xs ${lead.status === s.key ? `${s.color} text-white` : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                  data-testid={`detail-stage-${s.key}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Templates */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Template messaggi rapidi</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {Object.entries(TEMPLATES).map(([key, tpl]) => (
                <button
                  key={key}
                  onClick={() => copyTemplate(key)}
                  className="p-2.5 rounded-lg bg-muted/60 hover:bg-muted text-xs text-left flex items-center gap-2"
                  data-testid={`template-${key}`}
                >
                  <tpl.icon className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                  <span className="truncate font-medium">{tpl.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Reminder */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Bell className="w-3 h-3" /> Reminder
              </p>
              {!reminderOpen && (
                <button
                  onClick={() => setReminderOpen(true)}
                  className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/25 transition-colors"
                  data-testid="detail-reminder-open"
                >
                  + Imposta
                </button>
              )}
            </div>
            {reminderOpen && (
              <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 space-y-2" data-testid="detail-reminder-panel">
                <Input
                  type="datetime-local"
                  value={reminderAt}
                  onChange={(e) => setReminderAt(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="detail-reminder-datetime"
                />
                <Input
                  placeholder="Cosa vuoi ricordare? (opzionale)"
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  className="h-9 text-sm"
                  data-testid="detail-reminder-note"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => { setReminderOpen(false); setReminderAt(""); setReminderNote(""); }}
                    className="text-xs px-3 py-1.5 rounded-full text-muted-foreground hover:bg-muted"
                    data-testid="detail-reminder-cancel"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={saveReminder}
                    disabled={savingReminder || !reminderAt}
                    className="text-xs px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50 flex items-center gap-1"
                    data-testid="detail-reminder-save"
                  >
                    {savingReminder ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Salva reminder
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Add entry */}
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Aggiungi annotazione</p>
            <div className="flex items-center gap-2">
              <Select value={entryType} onValueChange={setEntryType}>
                <SelectTrigger className="w-[130px] h-9 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="call">📞 Chiamata</SelectItem>
                  <SelectItem value="message">💬 Messaggio</SelectItem>
                  <SelectItem value="followup">🔁 Follow-up</SelectItem>
                  <SelectItem value="note">📝 Nota</SelectItem>
                </SelectContent>
              </Select>
              <Input value={entryContent} onChange={(e) => setEntryContent(e.target.value)} placeholder="Cosa è successo…" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEntry(); } }} className="flex-1" data-testid="detail-entry-input" />
              <Button onClick={addEntry} disabled={saving || !entryContent.trim()} size="sm" className="rounded-lg bg-violet-500 hover:bg-violet-600 text-white h-9" data-testid="detail-entry-add">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div className="space-y-1.5 max-h-[150px] overflow-y-auto mt-2" data-testid="detail-entries-list">
              {entries.map(e => (
                <div key={e.entry_id} className="p-2 rounded-lg bg-muted/40 text-xs">
                  <p className="text-foreground">{e.content}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{new Date(e.created_at).toLocaleString("it-IT")}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SalesDashboardV2;
